import { maxAqiNext8h, fmtHour } from "../lib/aqi";
import { aqiCategory } from "../lib/coastal";
import { useT } from "../hooks/useLang";

const BAND_LABEL_TH: Record<string, string> = {
  "GOOD": "ดี",
  "MODERATE": "ปานกลาง",
  "UNHEALTHY · SG": "เสี่ยงต่อกลุ่มเปราะบาง",
  "UNHEALTHY": "ไม่ดีต่อสุขภาพ",
  "VERY UNHEALTHY": "แย่มาก",
  "HAZARDOUS": "อันตราย",
};

const BAND_ADVICE_TH: Record<string, string> = {
  "Outdoor activity normal.": "กิจกรรมกลางแจ้งได้ตามปกติ",
};

interface TrendPoint {
  at: string;
  aqi: number | null;
  pm25: number | null;
  /** false = observed history (last 24h), true = forecast (next 8h). */
  isForecast?: boolean;
}

export interface AqiTrend {
  station: string;
  category: "good" | "moderate" | "unhealthy-sg" | "unhealthy" | "very-unhealthy" | "hazardous" | null;
  current: { aqi: number | null; pm25: number | null; observedAt: string };
  /** Last 24h of observed readings, oldest → newest, ending at "now". Optional
   *  for backwards-compat with cached payloads that predate the history field. */
  history?: TrendPoint[];
  next8h: TrendPoint[];
  source: string;
}

interface Props {
  trend: AqiTrend | null;
  /** Real AQICN ground-station reading. When present (and it has an AQI), it
   *  drives the headline number/PM2.5/station name; the trend model supplies the
   *  forecast sparkline + 8h peak as context. Falls back to the model if absent. */
  station?: { aqi: number | null; pm25: number | null; name: string; observedAt: string } | null;
  loading: boolean;
}

const BAND: Record<
  NonNullable<AqiTrend["category"]>,
  { color: string; label: string; advice: string }
> = {
  // Colours reference CSS tokens defined in tokens.css (US EPA standard scale).
  good:            { color: "var(--aqi-good)",           label: "GOOD",            advice: "Outdoor activity normal." },
  moderate:        { color: "var(--aqi-moderate)",       label: "MODERATE",        advice: "Sensitive groups: limit prolonged outdoor exertion." },
  "unhealthy-sg":  { color: "var(--aqi-unhealthy-sg)",   label: "UNHEALTHY · SG",  advice: "Sensitive groups should reduce outdoor activity." },
  unhealthy:       { color: "var(--aqi-unhealthy)",      label: "UNHEALTHY",       advice: "Everyone: reduce outdoor exertion. Masks recommended." },
  "very-unhealthy":{ color: "var(--aqi-very-unhealthy)", label: "VERY UNHEALTHY",  advice: "Avoid outdoor exertion. N95 masks outdoors." },
  hazardous:       { color: "var(--aqi-hazardous)",      label: "HAZARDOUS",       advice: "Stay indoors. Run filtration. Cancel outdoor events." },
};

export function AqiBadge({ trend, station, loading }: Props) {
  const t = useT();

  // Headline prefers the real AQICN ground station; falls back to the Open-Meteo
  // trend model when the station has no reading.
  const useStation = station != null && station.aqi != null;
  const curAqi = useStation ? (station!.aqi as number) : (trend?.current.aqi ?? null);
  const curPm25 = useStation ? station!.pm25 : (trend?.current.pm25 ?? null);
  const curObservedAt = useStation ? station!.observedAt : (trend?.current.observedAt ?? "");
  const stationName = useStation ? station!.name : (trend?.station ?? "");
  const category = useStation ? aqiCategory(curAqi as number) : (trend?.category ?? null);

  if (loading || curAqi == null || category == null) {
    return (
      <div className="aqi-badge" aria-busy={loading}>
        <div className="aqi-bar" style={{ background: "var(--line-2)" }} />
        <div className="aqi-readout">
          <div className="eyebrow mono">{t("AIR QUALITY", "คุณภาพอากาศ")} · US AQI</div>
          <div className="aqi-num skeleton" style={{ width: 90, height: 56 }} />
          <div className="caption mono" style={{ color: "var(--text-3)" }}>
            {loading ? t("Connecting…", "กำลังเชื่อมต่อ…") : t("Unavailable", "ไม่พร้อมใช้งาน")}
          </div>
        </div>
      </div>
    );
  }

  const band = BAND[category];
  // Honest provenance: real sensor vs forecast model.
  const sourceLabel = useStation ? t("LIVE STATION", "สถานีตรวจวัดจริง") : t("MODEL", "แบบจำลอง");
  const peak = trend ? maxAqiNext8h(trend) : curAqi;
  const trending = peak - curAqi;
  const trendArrow = trending >= 4 ? "▲" : trending <= -4 ? "▼" : "→";
  const trendDir = trending >= 4 ? "rising" : trending <= -4 ? "falling" : "stable";
  const trendColor = trending >= 4 ? "var(--bad)" : trending <= -4 ? "var(--good)" : "var(--text-2)";

  // Build the unified timeline: PAST 24h (observed) → "now" → +8h forecast.
  // Each point carries `kind` so we can render history solid and forecast dashed
  // on one shared coordinate scale. Guards against missing/null fields.
  const history = trend?.history ?? [];
  const historyPts: Array<{ aqi: number; kind: "past" }> = history
    .filter((p): p is TrendPoint & { aqi: number } => typeof p.aqi === "number")
    .map((p) => ({ aqi: p.aqi, kind: "past" }));
  // The API repeats the "now" reading as next8h[0] (it == the last history
  // point). When we already have history, drop that duplicate so the two
  // segments meet at one shared x-position instead of stuttering.
  const next8h = trend?.next8h ?? [];
  const rawForecast = historyPts.length > 0 ? next8h.slice(1) : next8h;
  const forecastPts: Array<{ aqi: number; kind: "forecast" }> = rawForecast
    .filter((p): p is TrendPoint & { aqi: number } => typeof p.aqi === "number")
    .map((p) => ({ aqi: p.aqi, kind: "forecast" }));

  // "now" is the boundary: last history point if present, else current reading.
  // When history is empty we fall back to current + forecast (legacy behaviour).
  const timeline: Array<{ aqi: number; kind: "past" | "forecast" }> =
    historyPts.length > 0
      ? [...historyPts, ...forecastPts]
      : [
          { aqi: curAqi, kind: "past" as const },
          ...forecastPts,
        ];

  const series = timeline.map((p) => p.aqi);
  const maxVal = Math.max(...series, 50);
  const minVal = Math.min(...series, 0);
  const span = Math.max(1, maxVal - minVal);
  const w = 200;
  const h = 32;
  const n = timeline.length;
  const xAt = (i: number) => (n > 1 ? (i / (n - 1)) * w : w / 2);
  const yAt = (v: number) => h - ((v - minVal) / span) * h;

  // Index of the "now" boundary = last past point (forecast starts here).
  const nowIdx = Math.max(0, timeline.findIndex((p) => p.kind === "forecast") - 1);
  const lastPastIdx = (() => {
    for (let i = timeline.length - 1; i >= 0; i--) if (timeline[i].kind === "past") return i;
    return -1;
  })();

  const pointStr = (i: number) => `${xAt(i).toFixed(1)},${yAt(timeline[i].aqi).toFixed(1)}`;
  // Past polyline runs through every history point up to "now".
  const pastPts = lastPastIdx >= 0
    ? Array.from({ length: lastPastIdx + 1 }, (_, i) => pointStr(i)).join(" ")
    : "";
  // Forecast polyline starts at the now-boundary so the two segments meet.
  const fcStart = lastPastIdx >= 0 ? lastPastIdx : 0;
  const forecastStr = n > fcStart + 1
    ? Array.from({ length: n - fcStart }, (_, k) => pointStr(fcStart + k)).join(" ")
    : "";
  const nowX = xAt(nowIdx);
  const ref100Y = yAt(100);

  return (
    <section
      className="aqi-badge"
      role="status"
      aria-label={`Air quality ${band.label}, AQI ${curAqi}, ${useStation ? "ground station" : "model"}`}
      style={{ ["--aqi-color" as never]: band.color }}
    >
      <div className="aqi-bar" />
      <div className="aqi-readout">
        <div className="aqi-head">
          <span className="eyebrow mono">{t("AIR QUALITY", "คุณภาพอากาศ")} · US AQI</span>
          <span className={`aqi-source mono ${useStation ? "aqi-source--live" : ""}`} title={stationName}>
            {useStation ? "● " : ""}{sourceLabel}
          </span>
          <span className="aqi-band mono">{t(band.label, BAND_LABEL_TH[band.label] ?? band.label)}</span>
        </div>
        <div className="aqi-row">
          <div className="aqi-num" style={{ color: band.color }}>
            {Math.round(curAqi)}
          </div>
          <div className="aqi-stats mono">
            <div>
              <span className="caption">PM2.5</span>
              <strong>{(curPm25 ?? 0).toFixed(1)}</strong>
              <span className="caption" style={{ textTransform: "none" }}>µg/m³</span>
            </div>
            <div>
              <span className="caption">{t("PEAK 8H", "สูงสุด 8 ชม.")}</span>
              <strong style={{ color: trendColor }}>{Math.round(peak)}</strong>
              <span style={{ color: trendColor }} aria-label={trendDir}>{trendArrow}</span>
            </div>
          </div>
        </div>
        <svg viewBox={`0 0 ${w} ${h}`} className="aqi-spark" aria-hidden="true">
          {/* AQI = 100 (moderate ceiling) reference line */}
          <line x1="0" y1={ref100Y} x2={w} y2={ref100Y}
                stroke="var(--line-2)" strokeWidth="0.5" strokeDasharray="2 2" />
          {/* PAST 24h — observed, solid neutral ink */}
          {pastPts && (
            <polyline points={pastPts} stroke="var(--ink-2)" strokeWidth="1.5" fill="none"
                      strokeLinejoin="round" strokeLinecap="round" />
          )}
          {/* FORECAST +8h — band-coloured, dashed + lighter to read as projection */}
          {forecastStr && (
            <polyline points={forecastStr} stroke={band.color} strokeWidth="1.5" fill="none"
                      strokeDasharray="3 2" strokeLinejoin="round" strokeLinecap="round"
                      opacity="0.85" />
          )}
          {/* "now" boundary tick separating observed history from forecast */}
          {pastPts && forecastStr && (
            <line x1={nowX} y1="0" x2={nowX} y2={h}
                  stroke="var(--line-2)" strokeWidth="0.75" />
          )}
        </svg>
        <div className="aqi-foot">
          <span className="caption">{stationName}</span>
          <span className="caption mono">{curObservedAt ? fmtHour(curObservedAt) : ""}</span>
        </div>
        <div className="aqi-advice">{t(band.advice, BAND_ADVICE_TH[band.advice] ?? band.advice)}</div>
      </div>
    </section>
  );
}
